import React, { useState } from "react";
import { AddEditModal, ReactSelect } from "../../../index";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Formik, Form, ErrorMessage } from "formik";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { Option } from "src/Modal/AddOrUpdateAccessRightsModal/AccessRightsModal";
import { useTranslation } from "react-i18next";
import { useGetRolesQuery } from "@me-team/host/main/addEmployee/graphql/addEmployee.hooks";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "@me-team/common/src/services/ErrorService";
import { RoleProxy } from "@me-team/host/main/graphql/types";
import Spinner from "../../../Spinner/Spinner";
import { FormDataAccessRights, SelectedEmployee } from "../types";
import { addAccessRightsFormValidationSchema } from "../constants";
import { useGetCurrentBranchNameAndIdForRightModalQuery } from "@me-team/host/main/logbook/graphql/logbook.hooks";
import { useParams } from "react-router-dom";
import { cloneNameToDisplay } from "@me-team/common/src/components/Me-menu/utils/cloneNameUtils";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import AddAccessRightsFormEffects from "./AddAccessRightsFormEffects";

interface AddAccessRightsModalProps {
    isAddAccessRightsModal: boolean;
    onCloseAddAccessRightsModal: () => void;
    selectedEmployeeToAdd: SelectedEmployee;
    onAddAccessRightsEmployee: (values: FormDataAccessRights) => void;
    branchToAdd?: CurrentBranch;
    action?: "add" | "edit";
}

interface CurrentBranch {
    id: number;
    name: string;
}

const AddAccessRightsModal: React.FC<AddAccessRightsModalProps> = ({
    isAddAccessRightsModal,
    onCloseAddAccessRightsModal,
    selectedEmployeeToAdd,
    onAddAccessRightsEmployee,
    branchToAdd,
    action = "add",
}) => {
    const { t } = useTranslation();
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);

    const { data: currentBranchData, loading: currentBranchLoading } = useGetCurrentBranchNameAndIdForRightModalQuery({
        variables: { companyId: Number(currentCompanyId), branchId: Number(branchId) },
        context: {
            errorType: "local",
        },
        skip: !!branchToAdd || !currentCompanyId,
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const currentBranch: CurrentBranch = branchToAdd ? branchToAdd : currentBranchData?.user?.company.branches[0];

    const { data: rolesData, loading: roleLoading } = useGetRolesQuery({
        variables: {
            branchId: Number(currentBranch?.id),
            page: 1,
            itemsPerPage: 10000,
            excludeArchived: true,
        },
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const rolesOptions: Option[] =
        rolesData?.role?.roles
            ?.filter((role: RoleProxy) => !role.isOwner)
            .map((role: RoleProxy) => {
                return {
                    value: role?.id,
                    label: cloneNameToDisplay(role?.name, t),
                };
            }) || [];

    const initialValues: FormDataAccessRights = {
        employeeId: selectedEmployeeToAdd?.id ? selectedEmployeeToAdd.id : null,
        branchId: currentBranch?.id ? currentBranch.id : null,
        roleId: selectedEmployeeToAdd?.roleId ? selectedEmployeeToAdd.roleId : null,
    };

    if (roleLoading || currentBranchLoading) return <Spinner />;
    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={addAccessRightsFormValidationSchema(t)}
                enableReinitialize
                onSubmit={(values, { resetForm, setSubmitting }) => {
                    onAddAccessRightsEmployee(values);
                    resetForm();
                    setSubmitting(false);
                }}
            >
                {({ values, setFieldValue, handleSubmit, isSubmitting, resetForm }) => {
                    return (
                        <Form>
                            <AddAccessRightsFormEffects
                                selectedEmployeeToAdd={selectedEmployeeToAdd}
                                isAddAccessRightsModal={isAddAccessRightsModal}
                            />
                            <AddEditModal
                                isModalOpened={isAddAccessRightsModal}
                                setIsModalOpened={onCloseAddAccessRightsModal}
                                title={action === "edit" ? t("Edit access rights") : t("Add access rights")}
                                modalBodyClass="pb-0 px-0"
                            >
                                <Container className="pb-3 px-3">
                                    <Row className="m-0 mb-3">
                                        <Col md={3} className="mb-2 mb-md-0 text-start d-flex align-items-center">
                                            <label className="text-dark fs-7 fw-bold py-1">{t("Employee")}</label>
                                        </Col>
                                        <Col md={9} className="d-flex align-items-center ">
                                            <span className="fs-7 py-1">
                                                {selectedEmployeeToAdd?.name} {selectedEmployeeToAdd?.surname}
                                            </span>
                                        </Col>
                                    </Row>
                                    <Row className="m-0 mb-3">
                                        <Col md={3} className="mb-2 mb-md-0 text-start d-flex align-items-center">
                                            <label className="text-dark fs-7 fw-bold py-1">{t("Branch")}</label>
                                        </Col>
                                        <Col md={9} className="d-flex align-items-center ">
                                            <span className="fs-7 py-1">{currentBranch?.name}</span>
                                        </Col>
                                    </Row>
                                    <Row className="m-0 h-100" style={{ minHeight: "49px" }}>
                                        <Col md={3} className="mb-2 mb-md-0">
                                            <label
                                                htmlFor="editEmployee-role-field"
                                                className="text-dark fs-7 fw-bold py-1 h-100 d-flex align-items-center text-start required-label"
                                            >
                                                {t("Access rights (role)")}
                                            </label>
                                        </Col>
                                        <Col md={9} className="text-start">
                                            <ReactSelect
                                                id="AddAccessRightsModal-Id-field"
                                                name="roleId"
                                                placeholder={t("Select a role")}
                                                options={rolesOptions}
                                                value={rolesOptions.find((option) => option.value === values.roleId)}
                                                onChange={(selectedOption: Option) =>
                                                    setFieldValue("roleId", selectedOption?.value)
                                                }
                                                isSearchable={false}
                                            />
                                            <ErrorMessage name="roleId">
                                                {(msg) => <div className="text-danger fs-7 mt-1">{msg}</div>}
                                            </ErrorMessage>
                                        </Col>
                                    </Row>
                                </Container>
                                <Container className="p-6">
                                    <div className="d-flex justify-content-end mt-4 pt-2">
                                        <Button
                                            onClick={() => {
                                                resetForm();
                                                onCloseAddAccessRightsModal();
                                            }}
                                            variant="outline-primary"
                                            type="button"
                                            className="custom-btn fw-normal me-2"
                                        >
                                            {t("cancel")}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            type="button"
                                            className="custom-btn"
                                            onClick={() => handleSubmit()}
                                            disabled={isSubmitting}
                                        >
                                            {action === "edit" ? t("save") : t("add")}
                                        </Button>
                                    </div>
                                </Container>
                            </AddEditModal>
                        </Form>
                    );
                }}
            </Formik>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default AddAccessRightsModal;
